/**
 * Newsletter Form - Dual submission: Klaviyo + Shopify
 * 1. Creates profile & subscribes to Klaviyo email list
 * 2. Creates Shopify customer via hidden iframe (contact form)
 * Both run in parallel; success is shown when Klaviyo succeeds.
 */
(function() {
  'use strict';

  var KLAVIYO_COMPANY_ID = 'XPbsj5';
  var KLAVIYO_LIST_ID = 'VKs3Ef';
  var KLAVIYO_API_URL = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + KLAVIYO_COMPANY_ID;
  var KLAVIYO_API_REVISION = '2025-01-15';

  var SELECTORS = {
    form: '#footer-newsletter-form',
    input: '.newsletter-form__input',
    button: '.newsletter-form__button',
    buttonText: '.newsletter-form__button-text',
    buttonLoading: '.newsletter-form__button-loading',
    buttonIcon: '.btn-icon__icon',
    error: '.newsletter-form__error',
    success: '.newsletter-success',
    wrapper: '.newsletter-form-wrapper'
  };

  var CLASSES = {
    loading: 'is-loading',
    error: 'is-error',
    success: 'is-success',
    shake: 'newsletter-form--shake'
  };

  function init() {
    var form = document.querySelector(SELECTORS.form);
    if (!form) return;

    form.addEventListener('submit', handleSubmit);

    var input = form.querySelector(SELECTORS.input);
    if (input) {
      input.addEventListener('input', function() { clearError(form); });
    }
  }

  function handleSubmit(e) {
    e.preventDefault();

    var form = e.target;
    var input = form.querySelector(SELECTORS.input);
    var email = input?.value?.trim();

    if (!email || !isValidEmail(email)) {
      showError(form, window.newsletterMessages?.invalidEmail || 'Vul een geldig e-mailadres in');
      return;
    }

    setLoadingState(form, true);
    clearError(form);

    // Fire both in parallel
    submitToShopify(form, email);
    subscribeToKlaviyo(form, email);
  }

  // ── Klaviyo: create profile + subscribe to list ──
  function subscribeToKlaviyo(form, email) {
    fetch(KLAVIYO_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': KLAVIYO_API_REVISION
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: 'Website Footer',
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            }
          },
          relationships: {
            list: {
              data: {
                type: 'list',
                id: KLAVIYO_LIST_ID
              }
            }
          }
        }
      })
    })
    .then(function(response) {
      if (response.ok || response.status === 202) {
        showSuccess(form);
      } else {
        return response.json().then(function(data) {
          throw new Error(data?.errors?.[0]?.detail || 'Inschrijving mislukt');
        });
      }
    })
    .catch(function() {
      setLoadingState(form, false);
      showError(form, window.newsletterMessages?.error || 'Er ging iets mis. Probeer het opnieuw.');
    });
  }

  // ── Shopify: create customer via hidden iframe ──
  function submitToShopify(form, email) {
    var iframeName = 'newsletter-iframe-' + Date.now();

    var iframe = document.createElement('iframe');
    iframe.name = iframeName;
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    var hiddenForm = document.createElement('form');
    hiddenForm.method = 'POST';
    hiddenForm.action = '/contact#contact_form';
    hiddenForm.target = iframeName;
    hiddenForm.style.display = 'none';

    var formData = new FormData(form);
    formData.set('contact[email]', email);
    formData.set('contact[tags]', 'newsletter');

    for (var pair of formData.entries()) {
      var input = document.createElement('input');
      input.type = 'hidden';
      input.name = pair[0];
      input.value = pair[1];
      hiddenForm.appendChild(input);
    }

    document.body.appendChild(hiddenForm);
    hiddenForm.submit();

    // Clean up after 5s
    setTimeout(function() {
      iframe.remove();
      hiddenForm.remove();
    }, 5000);
  }

  function isValidEmail(email) {
    var emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  function setLoadingState(form, isLoading) {
    var button = form.querySelector(SELECTORS.button);
    var buttonText = form.querySelector(SELECTORS.buttonText);
    var buttonLoading = form.querySelector(SELECTORS.buttonLoading);
    var buttonIcon = form.querySelector(SELECTORS.buttonIcon);
    var input = form.querySelector(SELECTORS.input);

    if (isLoading) {
      form.classList.add(CLASSES.loading);
      button.disabled = true;
      input.disabled = true;
      buttonText.style.display = 'none';
      buttonIcon.style.display = 'none';
      buttonLoading.style.display = 'inline-flex';
    } else {
      form.classList.remove(CLASSES.loading);
      button.disabled = false;
      input.disabled = false;
      buttonText.style.display = '';
      buttonIcon.style.display = '';
      buttonLoading.style.display = 'none';
    }
  }

  function showError(form, message) {
    var errorEl = form.querySelector(SELECTORS.error);
    var input = form.querySelector(SELECTORS.input);

    if (errorEl) {
      errorEl.textContent = message;
      errorEl.style.display = 'block';
    }

    if (input) {
      input.classList.add(CLASSES.error);
    }

    form.classList.add(CLASSES.shake);
    setTimeout(function() { form.classList.remove(CLASSES.shake); }, 500);
  }

  function clearError(form) {
    var errorEl = form.querySelector(SELECTORS.error);
    var input = form.querySelector(SELECTORS.input);

    if (errorEl) {
      errorEl.textContent = '';
      errorEl.style.display = 'none';
    }

    if (input) {
      input.classList.remove(CLASSES.error);
    }
  }

  function showSuccess(form) {
    var wrapper = form.closest(SELECTORS.wrapper);
    var successEl = wrapper?.querySelector(SELECTORS.success);

    if (!wrapper || !successEl) return;

    form.style.opacity = '0';
    form.style.transform = 'translateY(-10px)';
    form.style.transition = 'opacity 0.3s ease, transform 0.3s ease';

    setTimeout(function() {
      form.style.display = 'none';

      successEl.style.display = 'block';
      successEl.style.opacity = '0';
      successEl.style.transform = 'translateY(10px)';

      successEl.offsetHeight;

      successEl.style.transition = 'opacity 0.4s ease, transform 0.4s ease';
      successEl.style.opacity = '1';
      successEl.style.transform = 'translateY(0)';

      wrapper.classList.add(CLASSES.success);
    }, 300);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
